import React from 'react';
import { Col, Row } from 'react-grid-system';
import { isSoonPeorderDate } from '@Utils/date/moment';
import Icon from '@Components/Icon/Icon';
import Divider from '@Components/Divider/Divider';
import Accordion from '@Components/Accordion/Accordion';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import useTranslate from '@Utils/hooks/useTranslate';
import { IAddress } from '@ApiModels/commerceProfile';
import { ICheckout } from '@Redux/modules/checkout/actions';
import { getShipmentArgs } from '../util';
import MapDetails from './MapDetails';
import LabeledInformation from './LabeledInformation';
import {
	AccordionContentWrapper,
	SectionHeader,
	DeliveryMethodWrapper,
	DeliveryMethodDescription,
	DeliveryMethodTitle,
	DescriptionWrapper,
} from '@Components/Card/cards/ShippingDetails/ShippingDetails.styled';

interface IDeviceDelivery {
	launchDate: string;
	pickupDelivery: boolean | undefined;
	preorderDate: string | undefined;
	selectedAddress: IAddress | undefined;
	showMap: boolean;
}

const DeviceDelivery = ({
	launchDate,
	pickupDelivery,
	preorderDate,
	selectedAddress,
	showMap,
}: IDeviceDelivery): JSX.Element => {
	const { translate } = useTranslate();

	const getShippingAddressSubtitle = (shipmentAddress: ICheckout['payload']['shipmentAddress']) => {
		if (pickupDelivery) {
			return translate('shipping-details.accordion.shipping-data.subtitle-pickup');
		}
		const args = getShipmentArgs(shipmentAddress);
		return translate(`shipping-details.accordion.shipping-data.subtitle-${args.length}`, ...args);
	};

	return (
		<Accordion
			title={translate('shipping-details.accordion.shipping-data.title')}
			subtitle={getShippingAddressSubtitle(selectedAddress)}
			subtitleColor="black54"
			icon="locationPinOutlined"
			accordionHeight={80}
		>
			<AccordionContentWrapper>
				<SectionHeader>{translate('shipping-details.accordion.delivery-method.section-header')}</SectionHeader>
				<Divider marginBottom={8} />

				<DeliveryMethodWrapper paddingBottom={pickupDelivery ? 16 : undefined}>
					<Icon name="location" fill="black38" width={32} height={32} />
					<DescriptionWrapper>
						<DeliveryMethodTitle>
							{translate(
								pickupDelivery
									? 'shipping-details.accordion.delivery-method.title.pickup'
									: 'shipping-details.accordion.delivery-method.title'
							)}
						</DeliveryMethodTitle>
						<ConditionalRender
							show={!pickupDelivery}
							onTrue={() => (
								<DeliveryMethodDescription>
									{translate('shipping-details.accordion.delivery-method.description')}
								</DeliveryMethodDescription>
							)}
						/>

						<ConditionalRender
							show={!!preorderDate && !pickupDelivery}
							onTrue={() => (
								<DeliveryMethodDescription>
									{isSoonPeorderDate(preorderDate ?? '')
										? translate('availability.preorder.subtitle.second-version')
										: translate(
												'shipping-details.accordion.delivery-method.preorder',
												preorderDate || '-'
										  )}
								</DeliveryMethodDescription>
							)}
						/>
						<ConditionalRender
							show={!!launchDate && !!pickupDelivery}
							onTrue={() => (
								<DeliveryMethodDescription>
									{translate('shipping-details.accordion.delivery-method.pickup', launchDate || '-')}
								</DeliveryMethodDescription>
							)}
						/>
					</DescriptionWrapper>
				</DeliveryMethodWrapper>

				<ConditionalRender
					show={!pickupDelivery}
					onTrue={() => (
						<>
							<SectionHeader>
								{translate('shipping-details.accordion.delivery-address.section-header')}
							</SectionHeader>
							<Divider marginBottom={8} />
							<Row>
								<Col xs={12} sm={6}>
									<ConditionalRender
										show={!!selectedAddress?.governorate}
										onTrue={() => (
											<LabeledInformation
												label={translate(
													'shipping-details.billing.shipping-data.data.governorate'
												)}
												data={selectedAddress?.governorate}
											/>
										)}
									/>
									<ConditionalRender
										show={!!selectedAddress?.wilayat}
										onTrue={() => (
											<LabeledInformation
												label={translate('shipping-details.billing.shipping-data.data.wilayat')}
												data={selectedAddress?.wilayat}
											/>
										)}
									/>
									<ConditionalRender
										show={!!selectedAddress?.city}
										onTrue={() => (
											<LabeledInformation
												label={
													!!selectedAddress?.area
														? translate(
																'shipping-details.billing.contact-data.data.city-area'
														  )
														: translate('shipping-details.billing.contact-data.data.city')
												}
												data={
													!!selectedAddress?.area
														? `${selectedAddress?.city} / ${selectedAddress?.area}`
														: selectedAddress?.city
												}
											/>
										)}
									/>
									<ConditionalRender
										show={!!selectedAddress?.way}
										onTrue={() => (
											<LabeledInformation
												label={translate('shipping-details.billing.shipping-data.data.way')}
												data={selectedAddress?.way}
											/>
										)}
									/>
									<ConditionalRender
										show={!!selectedAddress?.house}
										onTrue={() => (
											<LabeledInformation
												label={translate('deliver-address.text-field.house-info')}
												data={
													(selectedAddress?.house ?? '') +
													(selectedAddress?.house && selectedAddress?.apartment
														? '/' + selectedAddress.apartment
														: '')
												}
											/>
										)}
									/>
								</Col>

								<MapDetails selectedAddress={selectedAddress} showMap={showMap} />
							</Row>
						</>
					)}
				/>
			</AccordionContentWrapper>
		</Accordion>
	);
};

export default DeviceDelivery;
